/**
 * iOS Live Activities (ActivityKit) for followed matches.
 *
 * When a user taps "notify me" on a match we start a Live Activity with push
 * support (capacitor-live-activity plugin). iOS emits a per-activity APNs
 * token which we register with the backend so the 60s live-score poller can
 * push score / minute / status updates onto the Lock Screen + Dynamic Island
 * card, ending it at full-time.
 *
 * All calls no-op on web / Android so the shared bundle is unaffected.
 */
import { getDeviceId } from './usePushNotifications';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const STORE_KEY = 'sk_live_activities_v1'; // { [logicalId]: { activityId, matchId, static } }

const isIOS = () => {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    return Boolean(cap && typeof cap.getPlatform === 'function' && cap.getPlatform() === 'ios');
  } catch {
    return false;
  }
};

const loadPlugin = async () => {
  try {
    const mod = await import('capacitor-live-activity');
    return mod.LiveActivity || null;
  } catch {
    return null;
  }
};

// ---- local store of logicalId → { activityId, matchId, static } ------------
const readStore = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; }
};
const writeStore = (obj) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(obj)); } catch { /* noop */ }
};
const upsertStore = (logicalId, patch) => {
  const s = readStore();
  s[logicalId] = { ...(s[logicalId] || {}), ...patch };
  writeStore(s);
};
const removeStore = (logicalId) => {
  const s = readStore();
  delete s[logicalId];
  writeStore(s);
};

const abbr = (name) => {
  if (!name) return '';
  const clean = String(name).replace(/[^A-Za-z\s]/g, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  const base = parts.length > 1 ? parts.map((p) => p[0]).join('') : clean;
  return base.slice(0, 3).toUpperCase();
};

// ---- token listener (wired once) -------------------------------------------
let listenerWired = false;
const postRegister = async (body) => {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/api/live-activities/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch { /* analytics-grade: never throw */ }
};

export const initLiveActivities = async () => {
  if (!isIOS() || listenerWired) return;
  const LiveActivity = await loadPlugin();
  if (!LiveActivity) return;
  listenerWired = true;
  try {
    await LiveActivity.addListener('liveActivityPushToken', async (event) => {
      // event: { id (logicalId), activityId, token }
      const store = readStore();
      const entry = store[event.id] || {};
      upsertStore(event.id, { activityId: event.activityId });
      await postRegister({
        device_id: getDeviceId(),
        activity_id: event.activityId,
        logical_id: event.id,
        match_id: entry.matchId,
        token: event.token,
        static: entry.static || {},
      });
    });
  } catch { /* plugin unavailable */ }
};

/**
 * Start a Live Activity for a followed match.
 * @param {object} m - { fixture_id, home_team, away_team, home_logo, away_logo,
 *                        league_name, date, home_score?, away_score? }
 */
export const startMatchLiveActivity = async (m) => {
  if (!isIOS() || !m || m.fixture_id == null) return;
  const LiveActivity = await loadPlugin();
  if (!LiveActivity) return;
  await initLiveActivities();

  const logicalId = `match-${m.fixture_id}`;
  const staticValues = {
    homeName: String(m.home_team || 'Home'),
    awayName: String(m.away_team || 'Away'),
    homeAbbr: abbr(m.home_team),
    awayAbbr: abbr(m.away_team),
    homeCrest: String(m.home_logo || ''),
    awayCrest: String(m.away_logo || ''),
    competitionName: String(m.league_name || ''),
    kickoff: String(m.date || ''),
  };
  const contentState = {
    homeScore: String(m.home_score ?? 0),
    awayScore: String(m.away_score ?? 0),
    minute: '0',
    statusShort: m.status_short || 'NS',
    statusLabel: 'Upcoming',
  };

  upsertStore(logicalId, { matchId: Number(m.fixture_id), static: staticValues });

  try {
    const res = await LiveActivity.startActivityWithPush({
      id: logicalId,
      attributes: staticValues,
      contentState,
    });
    if (res && res.activityId) {
      upsertStore(logicalId, { activityId: res.activityId });
    }
  } catch {
    removeStore(logicalId);
  }
};

/** End + unregister a match's Live Activity (user unfollowed). */
export const stopMatchLiveActivity = async (fixtureId) => {
  if (!isIOS() || fixtureId == null) return;
  const LiveActivity = await loadPlugin();
  if (!LiveActivity) return;
  const logicalId = `match-${fixtureId}`;
  const entry = readStore()[logicalId] || {};
  try {
    await LiveActivity.endActivity({
      id: logicalId,
      contentState: { homeScore: '0', awayScore: '0', minute: '0', statusShort: 'FT', statusLabel: 'Ended' },
      dismissalPolicy: 'immediate',
    });
  } catch { /* already ended */ }
  if (entry.activityId && API_BASE) {
    try {
      await fetch(`${API_BASE}/api/live-activities/${entry.activityId}`, { method: 'DELETE' });
    } catch { /* noop */ }
  }
  removeStore(logicalId);
};
